import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from '@frontend/i18n';
import { useAlert } from '@frontend/design-system';
import {
  useCareCreditCreatePaymentIntent,
  CareCreditCreatePaymentIntentOptions,
} from './use-care-credit-create-payment-intent';

export type UseCareCreditMakePaymentMutationProps = CareCreditCreatePaymentIntentOptions;

export const useCareCreditMakePaymentMutation = (props: UseCareCreditMakePaymentMutationProps) => {
  const { locationId, person } = props;
  const queryClient = useQueryClient();

  const { isLocationLoaded, isPaymentIdLoaded } = useConfirmPaymentHelpers();

  const { createPaymentIntent } = useCareCreditCreatePaymentIntent(props);

  const invalidateCareCreditQueries = async () => {
    if (!locationId || !person?.personId) return;

    const mobilePhone = person.contactInfo?.find((contact) => contact.type === 'PHONE_MOBILE')?.destination;

    await queryClient.invalidateQueries({
      predicate: ({ queryKey }) => {
        if (queryKey[0] === 'careCreditPatientData') {
          return queryKey[1] === locationId && queryKey[3] === person.personId;
        }

        if (queryKey[0] !== 'care-credit-integration-v1') {
          /**
           * All schema queries will start with 'care-credit-integration-v1'
           */
          return false;
        }

        const schemaEndpointName = queryKey[1];
        const request = queryKey[2] as { locationId?: string; phoneNumber?: string; personId?: string } | undefined;

        if (schemaEndpointName === 'AccountLookup') {
          return request?.locationId === locationId && request.phoneNumber === mobilePhone;
        }

        if (schemaEndpointName === 'GeneratePatientDataPurl') {
          return request?.locationId === locationId && request.personId === person.personId;
        }

        // otherwise don't update
        return false;
      },
    });
  };

  const { mutateAsync: makePaymentMutation, isLoading: makingPayment } = useMutation({
    mutationFn: async () => {
      if (!isLocationLoaded(locationId)) {
        // alerts own errors
        return;
      }

      const { paymentId, status } = await createPaymentIntent();

      if (!isPaymentIdLoaded(paymentId)) {
        // alerts own errors
        return;
      }

      return { paymentId, status };
    },
    onSuccess: async (result) => {
      if (result?.paymentId && result.status === 'SUCCEEDED') await invalidateCareCreditQueries();
    },
  });

  return {
    makePaymentMutation,
    makingPayment,
  };
};

const useConfirmPaymentHelpers = () => {
  const alerts = useAlert();
  const { t } = useTranslation('payments');

  const errorAlert = (message: string) =>
    alerts.error({
      autoDismissAfter: 12000,
      message,
    });

  const isLocationLoaded = (locationId: string | undefined): locationId is string => {
    if (!locationId) {
      errorAlert(t('Location ID is required.'));
      return false;
    }
    return true;
  };

  const isPaymentIdLoaded = (paymentId: string | undefined): paymentId is string => {
    if (!paymentId) {
      errorAlert(t('Failed to create payment intent. Please try again.'));
      return false;
    }
    return true;
  };

  return {
    errorAlert,
    isPaymentIdLoaded,
    isLocationLoaded,
  };
};
